import { useMemo } from 'react';
import { useSuitabilityReports } from 'src/suitability-report/hooks/useSuitabilityReports';
import { useMeetingAgenda } from 'src/suitability-report/hooks/useMeetingAgendas';
import { useFactFinds } from 'src/suitability-report/hooks/useFactFinds';
import { BackgroundProcess, FileData } from 'src/data-capture/hooks/file-uploads/types';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useMeetingNotes } from 'src/suitability-report/hooks/useMeetingNotes';

type GeneratedDocument = {
  id: string;
  clientId: string;
  displayName: string;
  createdAt: string;
  documentType: 'suitability-report' | 'meeting-agenda' | 'fact-find' | 'meeting-note';
  fileId?: number;
  file?: FileData;
  backgroundProcess?: BackgroundProcess;
};

type UseGeneratedDocumentsReturn = {
  documents: GeneratedDocument[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

type UseGeneratedDocumentsOptions = {
  clientId?: string;
};

export const useGeneratedDocuments = ({
  clientId: overrideClientId,
}: UseGeneratedDocumentsOptions = {}): UseGeneratedDocumentsReturn => {
  const { clientId: defaultClientId } = useCurrentClient();

  const clientId = overrideClientId ?? defaultClientId;

  const {
    suitabilityReports,
    isLoading: suitabilityLoading,
    error: suitabilityError,
    refetch: refetchSuitability,
  } = useSuitabilityReports({ clientId });

  const {
    meetingAgendas,
    isLoading: agendasLoading,
    error: agendasError,
    refetch: refetchAgendas,
  } = useMeetingAgenda({ clientId });

  const {
    factFinds,
    isLoading: factFindsLoading,
    error: factFindsError,
    refetch: refetchFactFinds,
  } = useFactFinds();

  const {
    meetingNotes,
    isLoading: notesLoading,
    error: notesError,
    refetch: refetchNotes,
  } = useMeetingNotes();

  const documents = useMemo((): GeneratedDocument[] => {
    const allDocuments: GeneratedDocument[] = [];

    suitabilityReports.forEach(report => {
      allDocuments.push({
        ...report,
        displayName: report.displayName || report.reportFile.fileName,
        documentType: 'suitability-report',
        fileId: report.reportFileId,
        file: report.reportFile,
        clientId: clientId,
      });
    });

    meetingAgendas.forEach(agenda => {
      allDocuments.push({
        ...agenda,
        displayName: agenda.fileName || 'Meeting Agenda',
        documentType: 'meeting-agenda',
        clientId: clientId,
      });
    });

    factFinds.forEach(factFind => {
      allDocuments.push({
        ...factFind,
        displayName: factFind.fileName || 'Fact Find',
        documentType: 'fact-find',
        backgroundProcess: undefined,
        clientId: clientId,
      });
    });

    meetingNotes.forEach(meetingNote => {
      allDocuments.push({
        ...meetingNote,
        displayName: meetingNote.fileName || 'Meeting Note',
        documentType: 'meeting-note',
        fileId: meetingNote.templatisedFileId,
        file: meetingNote.templatisedFile,
        clientId: clientId,
      });
    });

    return allDocuments.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [suitabilityReports, meetingAgendas, factFinds, meetingNotes]);

  const isLoading = suitabilityLoading || agendasLoading || factFindsLoading || notesLoading;
  const error = (suitabilityError ?? agendasError ?? factFindsError ?? notesError) as Error | null;

  const refetch = async () => {
    await Promise.all([refetchSuitability(), refetchAgendas(), refetchFactFinds(), refetchNotes()]);
  };

  return {
    documents,
    isLoading,
    error,
    refetch,
  };
};
